import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Stack, Alert, CircularProgress, Pagination, Card, Chip } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { PETICION_ESTADO, PETICION_TIPO } from "../../constants";
import AuthService from "../../../../services/auth.service";
import DateUtils from "../../../../utils/DateUtils";
import { StockDisplay, TipoBadge, EstadoBadge } from "../ui";
import { getAlmacenColor, getBadgeStyles } from "../ui/badgeColors";

// Vista mobile de peticiones con cards paginadas
export default function PeticionesMobileList({ rows, loading, onAsignarme, onDesasignar, onMarcarPreparado, onTraspasar, onReferenciaClick, processingAction, getNombreAlmacen }) {
    const [page, setPage] = useState(1);
    const pageSize = 10;

    // Si cambian los rows (filtros), vuelve a la primera página
    useEffect(() => {
        setPage(1);
    }, [rows]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#cc071e" }} />
            </Box>
        );
    }

    if (!rows?.length) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                No hay datos.
            </Alert>
        );
    }

    const totalPages = Math.ceil(rows.length / pageSize);
    const start = (page - 1) * pageSize;
    const pageRows = rows.slice(start, start + pageSize);
    const usuarioActualId = AuthService.getCurrentUser()?.id;

    // Color del acento (borde izquierdo) alineado a la paleta corporativa unificada
    const estadoColors = {
        'NO_ATENDIDO': '#e74c3c',
        'EN_PROCESO': '#f39c12',
        'PREPARADO': '#27ae60',
    };

    return (
        <Box sx={{ width: "100%" }}>
            {/* Cards con sombra */}
            <Stack spacing={1.5}>
                {pageRows.map((r) => {
                    // Verificar qué acción específica está en proceso para esta petición
                    const asignandoEsta = processingAction === `${r.id}-asignar`;
                    const desasignandoEsta = processingAction === `${r.id}-desasignar`;
                    const preparandoEsta = processingAction === `${r.id}-preparado`;
                    const traspasandoEsta = processingAction === `${r.id}-traspasar`;

                    const estadoValue = r.estado;
                    const borderColor = estadoColors[estadoValue] || '#95a5a6';

                    // Resolver nombres de solicitante y reponedor
                    const solicitante = r.usuario?.username || "Desconocido";
                    const reponedor = r.reponedor?.username || "Sin asignar";

                    // Determinar qué acciones mostrar
                    const puedeAsignarse = estadoValue === PETICION_ESTADO.NO_ATENDIDO.value;
                    // Traspasar solo si es PETICION (no reposición), está NO_ATENDIDO, en almacén 03 y hay varios almacenes con stock
                    const almacenesConStock = r.stock?.filter(s => s.cantidad > 0).length || 0;
                    const puedeTraspasarse = r.tipo === PETICION_TIPO.PETICION.value
                        && estadoValue === PETICION_ESTADO.NO_ATENDIDO.value
                        && r.almacenesAsignados?.includes('03')
                        && almacenesConStock > 1;
                    const esReponedorAsignado = String(r.reponedor?.id) === String(usuarioActualId);
                    const puedeDesasignar =
                        estadoValue === PETICION_ESTADO.EN_PROCESO.value && esReponedorAsignado;
                    const puedeMarcarPreparado =
                        estadoValue === PETICION_ESTADO.EN_PROCESO.value && esReponedorAsignado;

                    return (
                        <Card
                            key={r.id}
                            sx={{
                                borderLeft: `4px solid ${borderColor}`,
                                boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)",
                                transition: "box-shadow 0.2s",
                                "&:hover": {
                                    boxShadow: "0 4px 6px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)",
                                },
                            }}
                        >
                            <Box sx={{ p: 1.5 }}>
                                {/* Header: Referencia + Badge */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                    <Typography
                                        onClick={() => onReferenciaClick?.(r.referencia)}
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 14,
                                            color: "#cc071e",
                                            letterSpacing: "0.5px",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            "&:hover": {
                                                color: "#a30618",
                                            },
                                        }}
                                    >
                                        {r.referencia}
                                    </Typography>
                                    <EstadoBadge estado={estadoValue} size="small" />
                                </Box>

                                {/* Nombre del producto */}
                                <Typography
                                    sx={{
                                        fontSize: 15,
                                        fontWeight: 600,
                                        color: "#343a40",
                                        mb: 1,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {r.nombreProducto}
                                </Typography>

                                {/* Información condensada con iconos */}
                                <Stack spacing={0.5}>
                                    {/* Unidades + Solicitante + Fecha */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 1,
                                            fontSize: 12,
                                            color: "#6c757d",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                            <InventoryIcon sx={{ fontSize: 14 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                                {r.unidades} ud
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 12 }}>·</Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                            <PersonIcon sx={{ fontSize: 14 }} />
                                            <Typography sx={{ fontSize: 12 }}>{solicitante}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 12 }}>·</Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                                            <CalendarTodayIcon sx={{ fontSize: 13 }} />
                                            <Typography sx={{ fontSize: 12 }}>
                                                {DateUtils.formatearFechaHora(r.creado)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Número de albarán (si existe) */}
                                    {r.numeroAlbaran && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 12,
                                                color: "#495057",
                                            }}
                                        >
                                            <DescriptionIcon sx={{ fontSize: 14 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                                Albarán: {r.numeroAlbaran}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Tipo de petición */}
                                    {(() => {
                                        const tipo = r.tipo || PETICION_TIPO.PETICION.value;
                                        return <TipoBadge tipo={tipo} size="small" />;
                                    })()}

                                    {/* Reponedor asignado */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                            fontSize: 12,
                                            color: reponedor === "Sin asignar" ? "#dc3545" : "#495057",
                                        }}
                                    >
                                        <SyncAltIcon sx={{ fontSize: 14 }} />
                                        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                            {reponedor}
                                        </Typography>
                                    </Box>

                                    {/* Almacenes asignados */}
                                    {r.almacenesAsignados && r.almacenesAsignados.length > 0 && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 12,
                                            }}
                                        >
                                            <WarehouseIcon sx={{ fontSize: 14, color: "#cc071e" }} />
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                {r.almacenesAsignados.map((almacen) => (
                                                    <Chip
                                                        key={almacen}
                                                        label={getNombreAlmacen(almacen) || almacen}
                                                        size="small"
                                                        sx={getBadgeStyles(getAlmacenColor(almacen), {
                                                            height: 20,
                                                            fontSize: 10,
                                                        })}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Fecha de asignación */}
                                    {r.fechaAsignacion && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 12,
                                                color: "#6c757d",
                                            }}
                                        >
                                            <CalendarTodayIcon sx={{ fontSize: 13 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                                Asignado: {DateUtils.formatearFechaHora(r.fechaAsignacion)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Fecha de preparado */}
                                    {r.fechaPreparado && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: 12,
                                                color: "#198754",
                                            }}
                                        >
                                            <CalendarTodayIcon sx={{ fontSize: 13 }} />
                                            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                                Preparado: {DateUtils.formatearFechaHora(r.fechaPreparado)}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Stock disponible */}
                                    <StockDisplay
                                        stock={r.stock}
                                        getNombreAlmacen={getNombreAlmacen}
                                        variant="mobile"
                                    />
                                </Stack>

                                {/* Botones de acción */}
                                {(puedeAsignarse || puedeTraspasarse || puedeDesasignar || puedeMarcarPreparado) && (
                                    <Stack
                                        direction="row"
                                        spacing={0.75}
                                        sx={{ justifyContent: "flex-start", mt: 1.5, flexWrap: "wrap", gap: 0.75 }}
                                    >
                                        {puedeAsignarse && (
                                            <Button
                                                size="small"
                                                startIcon={
                                                    asignandoEsta ? (
                                                        <CircularProgress size={14} />
                                                    ) : (
                                                        <PersonAddIcon sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                                variant="outlined"
                                                sx={{
                                                    fontSize: 12,
                                                    px: 1.25,
                                                    py: 0.5,
                                                    color: "#495057",
                                                    borderColor: "#495057",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    "&:hover": {
                                                        color: "#212529",
                                                        borderColor: "#212529",
                                                        backgroundColor: "rgba(0,0,0,0.04)",
                                                    },
                                                    "& .MuiButton-startIcon": { mr: 0.5 },
                                                }}
                                                onClick={() => onAsignarme(r.id)}
                                                disabled={asignandoEsta}
                                            >
                                                Asignarme
                                            </Button>
                                        )}

                                        {puedeTraspasarse && (
                                            <Button
                                                size="small"
                                                startIcon={
                                                    traspasandoEsta ? (
                                                        <CircularProgress size={14} />
                                                    ) : (
                                                        <CompareArrowsIcon sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                                variant="outlined"
                                                sx={{
                                                    fontSize: 12,
                                                    px: 1.25,
                                                    py: 0.5,
                                                    color: "#ffc107",
                                                    borderColor: "#ffc107",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    "&:hover": {
                                                        color: "#e0a800",
                                                        borderColor: "#e0a800",
                                                        backgroundColor: "rgba(255, 193, 7, 0.04)",
                                                    },
                                                    "& .MuiButton-startIcon": { mr: 0.5 },
                                                }}
                                                onClick={() => onTraspasar(r.id)}
                                                disabled={traspasandoEsta}
                                            >
                                                Traspasar
                                            </Button>
                                        )}

                                        {puedeDesasignar && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={
                                                    desasignandoEsta ? (
                                                        <CircularProgress size={14} />
                                                    ) : (
                                                        <PersonRemoveIcon sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                                sx={{
                                                    fontSize: 12,
                                                    px: 1.25,
                                                    py: 0.5,
                                                    color: "#6c757d",
                                                    borderColor: "#6c757d",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    "&:hover": {
                                                        color: "#495057",
                                                        borderColor: "#495057",
                                                        backgroundColor: "rgba(0,0,0,0.04)",
                                                    },
                                                    "& .MuiButton-startIcon": { mr: 0.5 },
                                                }}
                                                onClick={() => onDesasignar(r.id)}
                                                disabled={desasignandoEsta}
                                            >
                                                Desasignar
                                            </Button>
                                        )}

                                        {puedeMarcarPreparado && (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={
                                                    preparandoEsta ? (
                                                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                                                    ) : (
                                                        <TaskAltIcon sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                                sx={{
                                                    fontSize: 12,
                                                    px: 1.25,
                                                    py: 0.5,
                                                    backgroundColor: "#198754",
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    "&:hover": {
                                                        backgroundColor: "#157347",
                                                    },
                                                    "& .MuiButton-startIcon": { mr: 0.5 },
                                                }}
                                                onClick={() => onMarcarPreparado(r.id)}
                                                disabled={preparandoEsta}
                                            >
                                                Marcar preparado
                                            </Button>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Card>
                    );
                })}
            </Stack>

            {/* Paginación */}
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        size="small"
                    />
                </Box>
            )}
        </Box>
    );
}
